import { ChevronDown } from '@tamagui/lucide-icons';
import { Accordion } from '@unistocks/components/accordion/accordion';
import { Image } from '@unistocks/components/basic/image';
import { Group } from '@unistocks/components/group/group';
import { SyncedAssetDetails } from '@unistocks/features/portfolios/components/asset-settings/synced-asset-details';
import { AssetSettingsTabProps } from '@unistocks/features/portfolios/components/asset-settings/tab-props';
import { findExternalPortfolio } from '@unistocks/features/portfolios/components/asset-settings/utils';
import { t } from '@unistocks/localization';
import { SyncedAssetDto } from '@unistocks/proxy/axios-client';
import { Option } from '@unistocks/types';
import React, { useMemo } from 'react';
import { ListItem, Separator, Square } from 'tamagui';

export type SyncedAssetListItem = Option<{
  id: string;
  syncedAsset: SyncedAssetDto;
  accountName: string;
  accountType: string;
}> & {
  image: string;
};

export const ExternalHoldingsTab: React.FC<AssetSettingsTabProps> = ({ portfolio, asset }) => {
  const syncedAssetListItems = useMemo(
    () =>
      asset?.syncedAssets
        .map(syncedAsset => {
          const externalPortfolio = findExternalPortfolio(syncedAsset, portfolio);

          if (!externalPortfolio) return null;

          const accountType = t(`::Enum:ExternalAccountType:${externalPortfolio.portfolioType}`);

          return {
            key: externalPortfolio.externalAccountDisplayName,
            value: {
              id: `${externalPortfolio.id}-${syncedAsset.id}`,
              syncedAsset,
              accountName: externalPortfolio.name,
              accountType: accountType
            },
            image: externalPortfolio.externalAccountImageUri
          } as SyncedAssetListItem;
        })
        .filter(Boolean) as SyncedAssetListItem[] | undefined,
    [asset, portfolio]
  );

  if (!syncedAssetListItems?.length) return null;

  return (
    <Accordion
      collapsible
      defaultValue={
        syncedAssetListItems.length === 1 ? syncedAssetListItems[0].value.id : undefined
      }
    >
      <Group separator={<Separator />}>
        {syncedAssetListItems.map(item => {
          const {
            image,
            key,
            value: { id, accountName }
          } = item;

          return (
            <Group.Item key={key}>
              <Accordion.Item value={id}>
                <Accordion.Trigger>
                  {({ open }) => (
                    <ListItem
                      cursor={'pointer'}
                      icon={<Image rounded size={'$5'} source={image} />}
                      title={key}
                      subTitle={accountName}
                      iconAfter={
                        <Square transition={'quick'} rotate={open ? '180deg' : '0deg'}>
                          <ChevronDown size={'$2'} />
                        </Square>
                      }
                    />
                  )}
                </Accordion.Trigger>
                <Accordion.Content>
                  <SyncedAssetDetails asset={asset} syncedAsset={item} portfolio={portfolio} />
                </Accordion.Content>
              </Accordion.Item>
            </Group.Item>
          );
        })}
      </Group>
    </Accordion>
  );
};

ExternalHoldingsTab.displayName = 'ExternalHoldingsTab';
